import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { FillControl } from "../reusable/FillControl";
import { StrokeStyleControl } from "../reusable/StrokeStyleControl";
import { isMultiShapeSelection } from "./SvgDiagram2DMultiSelectPanel";
import { CORNER_STYLES } from "../../utils/svgDiagram2DModel";
import type { CornerStyle, Vec2 } from "../../utils/svgDiagram2DModel";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

const CORNER_STYLE_LABELS: Record<CornerStyle, string> = {
    round: "Round",
    bevel: "Bevel",
};

/**
 * Floating inspector for the selected polygon: edits its vertices (add/remove,
 * per-vertex X/Y), corner softening (radius + round/bevel), and stroke/fill
 * style. Rendered as an overlay inside the viewer (mirrors the rect inspector);
 * closes itself when no polygon is selected.
 */
export function SvgDiagram2DPolygonPanel() {
    const ctx = useSvgDiagram2D();
    const {
        doc,
        selectedPolygonIds,
        setSelectedPolygonId,
        updatePolygon,
        removePolygon,
        pushSnapshot,
    } = ctx;

    // A multi-shape selection is handled by the shared multi-select panel.
    if (isMultiShapeSelection(ctx)) return null;

    // The inspector edits the first selected polygon (matches the other panels).
    const polygon = doc.polygons.find((p) => p.id === selectedPolygonIds[0]);
    if (!polygon) return null;

    /** Snapshot before a discrete (single-commit) edit so it can be undone. */
    const edit = (partial: Parameters<typeof updatePolygon>[1]) => {
        pushSnapshot();
        updatePolygon(polygon.id, partial);
    };

    /** Replace one vertex's coordinate, snapshotting first. */
    const setVertex = (index: number, axis: "x" | "y", value: number) => {
        const points = polygon.points.map((p, i) =>
            i === index ? { ...p, [axis]: value } : p,
        );
        edit({ points });
    };

    /** Insert a new vertex after `index` (midpoint to the next, or offset). */
    const addVertexAfter = (index: number) => {
        const cur = polygon.points[index]!;
        const next = polygon.points[(index + 1) % polygon.points.length]!;
        const mid: Vec2 = {
            x: Math.round((cur.x + next.x) / 2),
            y: Math.round((cur.y + next.y) / 2),
        };
        const points = [
            ...polygon.points.slice(0, index + 1),
            mid,
            ...polygon.points.slice(index + 1),
        ];
        edit({ points });
    };

    /** Remove one vertex (kept at a minimum of 3 so it stays a polygon). */
    const removeVertex = (index: number) => {
        if (polygon.points.length <= 3) return;
        const points = polygon.points.filter((_, i) => i !== index);
        edit({ points });
    };

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>
                    Polygon properties
                </strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setSelectedPolygonId(null)}
                    title="Close panel"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            {/* --- Geometry --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Geometry</p>
                <label className="field-label">
                    Label
                    <input
                        type="text"
                        value={polygon.label}
                        onChange={(e) =>
                            updatePolygon(polygon.id, { label: e.target.value })
                        }
                    />
                </label>
                <p className="path-tool-meta" style={{ margin: "2px 0" }}>
                    {polygon.points.length} vertices
                </p>
                {polygon.points.map((pt, i) => (
                    <div
                        key={i}
                        className="field-row"
                        style={{ gap: 4, alignItems: "flex-end" }}
                    >
                        <label className="field-label" style={{ flex: 1 }}>
                            {`V${i + 1} X`}
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={pt.x}
                                onChange={(v) =>
                                    setVertex(i, "x", num(v, pt.x))
                                }
                            />
                        </label>
                        <label className="field-label" style={{ flex: 1 }}>
                            {`V${i + 1} Y`}
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={pt.y}
                                onChange={(v) =>
                                    setVertex(i, "y", num(v, pt.y))
                                }
                            />
                        </label>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => addVertexAfter(i)}
                            title="Add a vertex after this one"
                            style={{ padding: "2px 6px" }}
                        >
                            +
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => removeVertex(i)}
                            title="Remove this vertex"
                            disabled={polygon.points.length <= 3}
                            style={{ padding: "2px 6px" }}
                        >
                            <Trash2 size={14} aria-hidden />
                        </button>
                    </div>
                ))}
            </div>

            {/* --- Corners --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Corners</p>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Corner radius
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={polygon.cornerRadius}
                            onChange={(v) =>
                                edit({
                                    cornerRadius: Math.max(
                                        0,
                                        num(v, polygon.cornerRadius),
                                    ),
                                })
                            }
                        />
                    </label>
                    {polygon.cornerRadius > 0 && (
                        <label className="field-label">
                            Corner style
                            <select
                                value={polygon.cornerStyle}
                                onChange={(e) =>
                                    edit({
                                        cornerStyle: e.target
                                            .value as CornerStyle,
                                    })
                                }
                            >
                                {CORNER_STYLES.map((c) => (
                                    <option key={c} value={c}>
                                        {CORNER_STYLE_LABELS[c]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
            </div>

            {/* --- Style --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Style</p>
                <StrokeStyleControl
                    strokeStyleId={polygon.strokeStyleId}
                    strokeStyles={doc.strokeStyles}
                    onChange={(strokeStyleId) => edit({ strokeStyleId })}
                >
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Stroke
                            <input
                                type="color"
                                value={polygon.color}
                                onChange={(e) =>
                                    updatePolygon(polygon.id, {
                                        color: e.target.value,
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                step={0.1}
                                value={polygon.strokeWidth}
                                onChange={(v) =>
                                    edit({
                                        strokeWidth: Math.max(
                                            0.1,
                                            num(v, polygon.strokeWidth),
                                        ),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <label className="field-label">
                        Dash (e.g. "4 2", empty = solid)
                        <input
                            type="text"
                            value={polygon.dash}
                            onChange={(e) =>
                                updatePolygon(polygon.id, {
                                    dash: e.target.value,
                                })
                            }
                        />
                    </label>
                </StrokeStyleControl>
                <div
                    className="field-row"
                    style={{ gap: 8, alignItems: "center" }}
                >
                    <FillControl
                        fill={polygon.fill}
                        patterns={doc.patterns}
                        onChange={(fill) => edit({ fill })}
                        label="Polygon"
                    />
                    <CheckboxField
                        checked={polygon.visible}
                        onChange={(checked) =>
                            updatePolygon(polygon.id, { visible: checked })
                        }
                        label={
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                }}
                            >
                                {polygon.visible ? (
                                    <Eye size={14} aria-hidden />
                                ) : (
                                    <EyeOff size={14} aria-hidden />
                                )}
                                Visible
                            </span>
                        }
                        inline
                    />
                </div>
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removePolygon(polygon.id)}
                    title="Delete this polygon"
                >
                    <Trash2 size={14} aria-hidden /> Delete polygon
                </button>
            </div>
        </div>
    );
}
